/**
 * Async option loaders consumed by SearchableSelect / BulkSpreadsheet
 * across the panel forms. Each loader hits the matching CRUD service's
 * `.all()` and maps the rows to `{ value, label }`.
 *
 * Loaders return `[]` on failure so the form still renders — the caller
 * surfaces the error via toast.
 */
import type { SearchableSelectOption } from '@kss/ui';
import {
  menuService, warehouseService, employeeService, roleService, tenantService,
} from '@kss/api';

function safeAll<T>(svc: { all: () => Promise<T[]> }): Promise<T[]> {
  return svc.all().catch(() => [] as T[]);
}

export const loadMenuOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await safeAll(menuService);
  return rows.map((r) => ({
    value: (r as { menuId?: string }).menuId ?? '',
    label: (r as { name?: string }).name ?? '(unnamed)',
  })).filter((o) => o.value);
};

export const loadWarehouseOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await safeAll(warehouseService);
  return rows.map((r) => ({
    value: (r as { warehouseId?: string }).warehouseId ?? '',
    label: (r as { name?: string }).name ?? '(unnamed)',
  })).filter((o) => o.value);
};

export const loadEmployeeOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await safeAll(employeeService);
  return rows.map((r) => {
    const x = r as { employeeId?: string; firstName?: string; lastName?: string; employeeCode?: string };
    return {
      value: x.employeeId ?? '',
      label: [x.firstName, x.lastName].filter(Boolean).join(' ') || x.employeeCode || '(unnamed)',
      description: x.employeeCode,
    };
  }).filter((o) => o.value);
};

export const loadRoleOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await safeAll(roleService);
  return rows.map((r) => {
    const x = r as { roleId?: string; code?: string; name?: string; description?: string };
    return {
      value: x.code ?? x.roleId ?? '',
      label: x.name ?? x.code ?? '(unnamed)',
      description: x.description,
    };
  }).filter((o) => o.value);
};

export const loadTenantOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await safeAll(tenantService);
  return rows.map((r) => {
    const x = r as { tenantId?: string; name?: string; code?: string };
    return {
      value: x.tenantId ?? '',
      label: x.name ?? x.code ?? '(unnamed)',
      description: x.code,
    };
  }).filter((o) => o.value);
};
